import { isEmpty } from '@fruitful/fruitils';
import {
    AccountOwnership,
    InvestmentManagementAccountType,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountDetails,
    type MoneyMapBankAccountDetails,
    type MoneyMapIMAccountDetails,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_CASH_ACCOUNT_OPTION,
    NEW_IM_ACCOUNT_OPTION,
    SupportedApexAccountRegistrationType,
    YesNo,
} from '@fruitful/types';
import { startCase } from 'lodash';
import { PICKER_PLACEHOLDER } from 'smoothie';
import { type z, ZodIssueCode } from 'zod';

import type {
    AccountFormType,
    BaseAccountSettingsFormSchema,
} from '../components/forms/AccountSettingsFormModule';

type ZodCtx = z.RefinementCtx;

export const validateRequiredFields = (
    accountFormType: AccountFormType,
    fields: Array<[string, unknown]>,
    ctx: ZodCtx,
): boolean => {
    let isValid = true;

    fields.forEach(([field, value]) => {
        if (isEmpty(value) || value === PICKER_PLACEHOLDER) {
            isValid = false;
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: [`${accountFormType}Settings.${field}`],
                message: `${startCase(field)} is required`,
            });
        }
    });

    return isValid;
};

export const validateAccountNameLength = (
    accountFormType: AccountFormType,
    ctx: ZodCtx,
    accountName?: string,
): boolean => {
    const isValid = (accountName?.length ?? 0) <= 20;

    if (!isValid) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            path: [`${accountFormType}Settings.name`],
            message: 'Account name must be at most 20 characters',
        });
    }

    return isValid;
};

/** When the transfer row is a proposed/new cash account, validate fields; skipped for backdoor Roth (Trad IRA row). */
export const validateTransferCashAccountFieldsWhenNotBackdoorRoth = (
    isBackdoorRothPair: boolean,
    transferAccountSettings: BaseAccountSettingsFormSchema | undefined,
    transferAccount: MoneyMapAccountWithAccountDetails | undefined,
    ctx: ZodCtx,
): boolean => {
    const isNewCashAccountForTransfer =
        transferAccountSettings?.moneyMapAccountId ===
        NEW_CASH_ACCOUNT_OPTION.value;
    const isProposedTransferCashAccount =
        transferAccount?.status === MoneyMapAccountStatus.PROPOSED &&
        transferAccount.type === MoneyMapAccountType.INTERNAL_BANKING;

    if (
        isBackdoorRothPair ||
        !(isNewCashAccountForTransfer || isProposedTransferCashAccount)
    ) {
        return true;
    }

    return (
        validateRequiredFields(
            'transferAccount',
            [
                ['name', transferAccountSettings?.name],
                ['ownership', transferAccountSettings?.ownership],
                ['category', transferAccountSettings?.category],
                ['virtualCard', transferAccountSettings?.virtualCard],
                ['physicalCard', transferAccountSettings?.physicalCard],
            ],
            ctx,
        ) &&
        validateAccountNameLength(
            'transferAccount',
            ctx,
            transferAccountSettings?.name,
        )
    );
};

export const validateIndividualAccountOwnership = (
    accountFormType: AccountFormType,
    primaryUserId: string | undefined,
    ctx: ZodCtx,
): boolean => {
    const isValid =
        !isEmpty(primaryUserId) && primaryUserId !== PICKER_PLACEHOLDER;

    if (!isValid) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            path: [`${accountFormType}Settings.primaryUserId`],
            message: 'Primary user is required for individual accounts',
        });
    }

    return isValid;
};

const isProposedAccountWithPhysicalCard = (
    account: MoneyMapAccountWithAccountDetails,
): boolean =>
    account.status === MoneyMapAccountStatus.PROPOSED &&
    account.type === MoneyMapAccountType.INTERNAL_BANKING &&
    (account.accountDetails as MoneyMapBankAccountDetails).hasPhysicalCard;

export const canProposePhysicalCard = (
    accounts: MoneyMapAccountWithAccountDetails[],
    isSoloMembership: boolean,
    accountOwnership: AccountOwnership,
    primaryUserId?: string,
): boolean => {
    if (isSoloMembership) {
        // For solo membership, make sure no other accounts have proposed cards
        return (
            accounts.find((account) =>
                isProposedAccountWithPhysicalCard(account),
            ) == null
        );
    }

    const proposedJointAccountWithCard = accounts.find(
        (account) =>
            isProposedAccountWithPhysicalCard(account) &&
            account.createParameters?.ownership === AccountOwnership.JOINT,
    );

    if (proposedJointAccountWithCard != null) {
        // If joint account has proposed card, cannot propose another one (for either a joint or individual account)
        return false;
    }

    const proposedIndividualAccountsWithCard = accounts.filter((account) =>
        isProposedAccountWithPhysicalCard(account),
    );

    if (accountOwnership === AccountOwnership.JOINT) {
        // If making a joint account, cannot propose card if any proposed individual accounts already have a card
        return proposedIndividualAccountsWithCard.length === 0;
    }

    // Can propose individual account with card for member if one does not already exist
    return (
        proposedIndividualAccountsWithCard.find(
            (account) =>
                account.createParameters?.primaryUserId === primaryUserId,
        ) == null
    );
};

const isPhysicalCardSelectionValidForAccount = (
    accounts: MoneyMapAccountWithAccountDetails[],
    isSoloMembership: boolean,
    accountSettings?: BaseAccountSettingsFormSchema,
    selectedAccount?: MoneyMapAccountWithAccountDetails,
): boolean => {
    if (
        isEmpty(accountSettings) ||
        isEmpty(accountSettings?.physicalCard) ||
        accountSettings?.physicalCard === YesNo.NO
    ) {
        // If not selecting physical card, return true
        return true;
    }

    const existingAccountAlreadyHasPhysicalCard =
        selectedAccount?.type === MoneyMapAccountType.INTERNAL_BANKING &&
        (selectedAccount.accountDetails as MoneyMapBankAccountDetails)
            .hasPhysicalCard;

    if (existingAccountAlreadyHasPhysicalCard) {
        // If editing an account that already has a physical card selected, return true
        return true;
    }

    // Otherwise, evaluate if a card can be proposed on a new account
    return canProposePhysicalCard(
        accounts,
        isSoloMembership,
        accountSettings?.ownership as AccountOwnership,
        accountSettings?.primaryUserId,
    );
};

export const validatePhysicalCardLimits = (
    accountFormType: AccountFormType,
    accounts: MoneyMapAccountWithAccountDetails[],
    isSoloMembership: boolean,
    accountSettings: BaseAccountSettingsFormSchema | undefined,
    selectedAccount: MoneyMapAccountWithAccountDetails | undefined,
    ctx: ZodCtx,
): boolean => {
    const isValid = isPhysicalCardSelectionValidForAccount(
        accounts,
        isSoloMembership,
        accountSettings,
        selectedAccount,
    );

    if (!isValid) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            path: [`${accountFormType}Settings.physicalCard`],
            message:
                'Cannot propose more than maximum number of physical cards',
        });
    }

    return isValid;
};

export const validateTransferPhysicalCardLimitsUnlessBackdoorRoth = (
    isBackdoorRothPair: boolean,
    accounts: MoneyMapAccountWithAccountDetails[],
    isSoloMembership: boolean,
    transferAccountSettings: BaseAccountSettingsFormSchema | undefined,
    transferAccount: MoneyMapAccountWithAccountDetails | undefined,
    ctx: ZodCtx,
): boolean =>
    isBackdoorRothPair
        ? true
        : validatePhysicalCardLimits(
              'transferAccount',
              accounts,
              isSoloMembership,
              transferAccountSettings,
              transferAccount,
              ctx,
          );

export const validateTransferIndividualOwnershipUnlessBackdoorRoth = (
    isBackdoorRothPair: boolean,
    transferAccountSettings: BaseAccountSettingsFormSchema | undefined,
    ctx: ZodCtx,
): boolean =>
    isBackdoorRothPair
        ? true
        : transferAccountSettings?.ownership === AccountOwnership.INDIVIDUAL
          ? validateIndividualAccountOwnership(
                'transferAccount',
                transferAccountSettings?.primaryUserId,
                ctx,
            )
          : true;

export const validateTransferAccountSettingsExist = (
    transferAccountSettings: BaseAccountSettingsFormSchema | undefined,
    ctx: ZodCtx,
    emptyMessage = 'Must specify a transfer account for income split',
): boolean => {
    const isValid = !isEmpty(transferAccountSettings);

    if (!isValid) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            path: ['transferAccountSettings.moneyMapAccountId'],
            message: emptyMessage,
        });
    }

    return isValid;
};

export const validateBackdoorRothTradAccount = (
    transferAccountSettings: BaseAccountSettingsFormSchema | undefined,
    accountSettings: BaseAccountSettingsFormSchema,
    transferAccount: MoneyMapAccountWithAccountDetails | undefined,
    ctx: ZodCtx,
): boolean => {
    if (
        !validateTransferAccountSettingsExist(
            transferAccountSettings,
            ctx,
            'Select a Traditional IRA, Rollover IRA, or new IM/Apex account for the backdoor pair',
        )
    ) {
        return false;
    }

    const settings = transferAccountSettings as BaseAccountSettingsFormSchema;

    if (settings.ownership === AccountOwnership.INDIVIDUAL) {
        const ownerOk = validateIndividualAccountOwnership(
            'transferAccount',
            settings.primaryUserId,
            ctx,
        );
        if (!ownerOk) {
            return false;
        }
    }

    const isNewImTrad =
        settings.moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value;
    const isNewApexTrad =
        settings.moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value;
    const isProposedApexTrad =
        transferAccount?.status === MoneyMapAccountStatus.PROPOSED &&
        transferAccount.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;
    const isProposedImTrad =
        transferAccount?.status === MoneyMapAccountStatus.PROPOSED &&
        transferAccount.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT;

    if (isNewImTrad) {
        const fieldsValid = validateRequiredFields(
            'transferAccount',
            [
                ['name', settings.name],
                ['ownership', settings.ownership],
                ['category', settings.category],
                ['investmentAccountType', settings.investmentAccountType],
                ['portfolio', settings.portfolio],
                ['riskLevel', settings.riskLevel],
            ],
            ctx,
        );
        if (!fieldsValid) {
            return false;
        }
        if (
            settings.investmentAccountType !==
            InvestmentManagementAccountType.IRA
        ) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['transferAccountSettings.investmentAccountType'],
                message:
                    'Backdoor Roth requires a Traditional IRA (IM account type IRA).',
            });
            return false;
        }
        if (
            settings.ownership === AccountOwnership.INDIVIDUAL &&
            settings.primaryUserId !== accountSettings.primaryUserId
        ) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['transferAccountSettings.primaryUserId'],
                message: 'Traditional IRA owner must match Roth IRA owner',
            });
            return false;
        }
        return true;
    }

    if (isNewApexTrad || isProposedApexTrad) {
        const fieldsValid = validateRequiredFields(
            'transferAccount',
            [
                ['name', settings.name],
                ['ownership', settings.ownership],
                [
                    'apexInvestmentAccountType',
                    settings.apexInvestmentAccountType,
                ],
            ],
            ctx,
        );
        if (!fieldsValid) {
            return false;
        }
    }

    if (isProposedImTrad) {
        const imType = (
            transferAccount.accountDetails as MoneyMapIMAccountDetails
        ).accountType;
        if (imType !== InvestmentManagementAccountType.IRA) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['transferAccountSettings.moneyMapAccountId'],
                message:
                    'Backdoor Roth requires a proposed IM account with type IRA.',
            });
            return false;
        }
        const fieldsValid = validateRequiredFields(
            'transferAccount',
            [
                ['name', settings.name],
                ['ownership', settings.ownership],
                ['category', settings.category],
                ['investmentAccountType', settings.investmentAccountType],
                ['portfolio', settings.portfolio],
                ['riskLevel', settings.riskLevel],
            ],
            ctx,
        );
        if (!fieldsValid) {
            return false;
        }
        if (
            settings.investmentAccountType !==
            InvestmentManagementAccountType.IRA
        ) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['transferAccountSettings.investmentAccountType'],
                message:
                    'Backdoor Roth requires a Traditional IRA (IM account type IRA).',
            });
            return false;
        }
    }

    if (
        transferAccount?.status === MoneyMapAccountStatus.EXISTS &&
        !isNewApexTrad
    ) {
        if (
            transferAccount.type ===
            MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT
        ) {
            const at = (
                transferAccount.accountDetails as MoneyMapApexAccountDetails
            ).apexAccountType;
            if (
                at !==
                    SupportedApexAccountRegistrationType.RegistrationIraTraditional &&
                at !==
                    SupportedApexAccountRegistrationType.RegistrationIraRollover
            ) {
                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    path: ['transferAccountSettings.moneyMapAccountId'],
                    message:
                        'Backdoor Roth requires a Traditional or Rollover IRA.',
                });
                return false;
            }
        } else if (
            transferAccount.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT
        ) {
            const imType = (
                transferAccount.accountDetails as MoneyMapIMAccountDetails
            ).accountType;
            if (imType !== InvestmentManagementAccountType.IRA) {
                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    path: ['transferAccountSettings.moneyMapAccountId'],
                    message:
                        'Backdoor Roth requires an IM account with type IRA.',
                });
                return false;
            }
        } else {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['transferAccountSettings.moneyMapAccountId'],
                message: 'Invalid account for backdoor Roth Traditional IRA.',
            });
            return false;
        }
    }

    if (
        settings.ownership === AccountOwnership.INDIVIDUAL &&
        settings.primaryUserId !== accountSettings.primaryUserId
    ) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            path: ['transferAccountSettings.primaryUserId'],
            message: 'Traditional IRA owner must match Roth IRA owner',
        });
        return false;
    }

    const apexType =
        (
            transferAccount?.accountDetails as
                | MoneyMapApexAccountDetails
                | undefined
        )?.apexAccountType ?? settings.apexInvestmentAccountType;

    const isApexTransferContext =
        isNewApexTrad ||
        isProposedApexTrad ||
        transferAccount?.type ===
            MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;

    if (isApexTransferContext) {
        if (
            apexType != null &&
            apexType !==
                SupportedApexAccountRegistrationType.RegistrationIraTraditional &&
            apexType !==
                SupportedApexAccountRegistrationType.RegistrationIraRollover
        ) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['transferAccountSettings.apexInvestmentAccountType'],
                message: 'Backdoor Roth requires a Traditional or Rollover IRA',
            });
            return false;
        }
    }

    return true;
};

/**
 * Transfer row for goal + other-account rules: backdoor Traditional IRA validation,
 * or (goals only) income-split transfer requirement.
 */
export const validateTransferAccountRowForMoneyMapRuleForm = (
    ctx: ZodCtx,
    options: {
        isBackdoorRothPair: boolean;
        /** Goal form: `shouldUseTransferAccount` when not backdoor */
        isIncomeSplitTransferRequired: boolean;
        transferAccountSettings: BaseAccountSettingsFormSchema | undefined;
        accountSettings: BaseAccountSettingsFormSchema;
        transferAccount: MoneyMapAccountWithAccountDetails | undefined;
    },
): boolean => {
    const {
        isBackdoorRothPair,
        isIncomeSplitTransferRequired,
        transferAccountSettings,
        accountSettings,
        transferAccount,
    } = options;
    if (isBackdoorRothPair) {
        return validateBackdoorRothTradAccount(
            transferAccountSettings,
            accountSettings,
            transferAccount,
            ctx,
        );
    }
    if (isIncomeSplitTransferRequired) {
        return validateTransferAccountSettingsExist(
            transferAccountSettings,
            ctx,
        );
    }
    return true;
};
